
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import * as api from '../services/api';

interface UnreadMessagesContextType {
    totalUnreadCount: number;
    conversationsWithUnread: Set<string>;
    refreshUnreadCounts: () => Promise<void>;
}

const UnreadMessagesContext = createContext<UnreadMessagesContextType | undefined>(undefined);

export const UnreadMessagesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);
    const [conversationsWithUnread, setConversationsWithUnread] = useState<Set<string>>(new Set());
    const isRefreshingRef = useRef(false);

    const refreshUnreadCounts = useCallback(async () => {
        // Nicht versuchen, wenn offline oder bereits ein Sync läuft
        if (!navigator.onLine || isRefreshingRef.current) return;

        if (!user) {
            setTotalUnreadCount(0);
            setConversationsWithUnread(new Set());
            return;
        }

        isRefreshingRef.current = true;
        try {
            let count = 0;
            const unreadConvoIds = new Set<string>();

            const conversations = await api.getConversations(user.role, user.clientId);
            
            conversations.forEach(c => {
                if (c.unreadCount > 0) {
                    count += c.unreadCount;
                    unreadConvoIds.add(c.id);
                }
            });

            setTotalUnreadCount(count);
            setConversationsWithUnread(unreadConvoIds);
        } catch (error: unknown) {
            const err = error as Error;
            // AbortError ist meist harmlos (Browser stoppt Request bei Navigation)
            if (err.name === 'AbortError' || err.message?.includes('signal is aborted')) {
                // Keine Fehlermeldung nötig
                return;
            }

            console.group("UnreadMessages Sync Status");
            console.warn(`Zähler-Sync pausiert: ${err.message || 'Netzwerk busy'}`);
            console.groupEnd();
        } finally {
            isRefreshingRef.current = false;
        }
    }, [user]);

    useEffect(() => {
        refreshUnreadCounts();
        const interval = setInterval(refreshUnreadCounts, 30000);
        return () => {
            clearInterval(interval);
            isRefreshingRef.current = false;
        };
    }, [refreshUnreadCounts]);

    return (
        <UnreadMessagesContext.Provider value={{ totalUnreadCount, conversationsWithUnread, refreshUnreadCounts }}>
            {children}
        </UnreadMessagesContext.Provider>
    );
};

export const useUnreadMessages = (): UnreadMessagesContextType => {
    const context = useContext(UnreadMessagesContext);
    if (!context) {
        throw new Error('useUnreadMessages must be used within an UnreadMessagesProvider');
    }
    return context;
};
